"use client"

import {
  TableSkeleton as BaseTableSkeleton,
  InfiniteTable,
} from "@/app/components/infinite-table/infinite-table"
import { HeaderRow } from "@/app/components/table"
import type { NftSearchItem } from "@anaconda/core/nft/search/types"
import { reatomComponent } from "@reatom/react"
import { EmptyMessage } from "../empty-message"
import { useColumns } from "./columns"
import { useTableItems } from "./use-table-items.reatom"

const getItemKey = (item: NftSearchItem) => item.id

// Define components first before referencing them
const TableHeaderRow = reatomComponent(() => {
  const columns = useColumns()

  return (
    <HeaderRow>
      {columns.map((column, index) => (
        <column.Header key={index} />
      ))}
    </HeaderRow>
  )
}, "TableHeaderRow")

const RowContent = reatomComponent<{ content: NftSearchItem }>(
  ({ content }) => {
    const columns = useColumns()

    return (
      <>
        {columns.map((column, index) => (
          <column.Content key={index} {...content} />
        ))}
      </>
    )
  },
  "RowContent",
)

const RowSkeleton = reatomComponent(() => {
  const columns = useColumns()

  return (
    <>
      {columns.map((column, index) => (
        <column.Skeleton key={index} />
      ))}
    </>
  )
}, "RowSkeleton")

// Now reference them after they're defined
const COMPONENTS = {
  HeaderRow: TableHeaderRow,
  RowContent,
  RowSkeleton,
  EmptyMessage,
} as const

export const TableView = reatomComponent(() => {
  const { items, loadNext } = useTableItems()

  return (
    <div className="flex-1">
      <InfiniteTable
        getItemKey={getItemKey}
        items={items}
        loadNext={loadNext}
        components={COMPONENTS}
        initialItemCount={10}
        dataTestId="collection-table-view"
      />
    </div>
  )
}, "TableView")

export function TableSkeleton() {
  return (
    <BaseTableSkeleton HeaderRow={TableHeaderRow} RowSkeleton={RowSkeleton} />
  )
}
